/**
 * Web component: In-grid banner
 * -----------------------------------------------------------------------------
 * In-grid banner functionality.
 * - Displays an image or video banner with a title, subtitle, and
 *   call-to-action button.
 *
 * @param {String} ctaLabel - Label for the call-to-action button.
 * @param {String} ctaUrl - URL for the call-to-action button.
 * @param {String} mobSrc - Source URL for the image or video on mobile.
 * @param {String} src - Source URL for the image or video on desktop.
 * @param {String} subtitle - Subtitle of the banner.
 * @param {String} title - Title of the banner.
 * @param {String} type - Type of media (image or video).
 * @param {String} ctaStyle - Style of CTA button ('light' or 'dark').
 */
class InGridBanner extends HTMLElement {

  /**
   * Custom element constructor.
   */
  constructor() {
    super();
  }

  /**
   * Mounted.
   */
  connectedCallback() {
    this.render();
  }

  /**
   * Render the content of the custom element.
   */
  render() {

    /**
     * Get attributes.
     */
    const type = this.getAttribute('type');
    const title = this.getAttribute('title');
    const ctaLabel = this.getAttribute('cta-label');
    const subtitle = this.getAttribute('subtitle');
    const ctaUrl = this.getAttribute('cta-url');
    const ctaStyle = this.getAttribute('cta-style') || 'light';
    let src = this.getAttribute('src');
    let mobSrc = this.getAttribute('mob-src');

    /**
     * Use other source as fallback if not defined.
     */
    if (!mobSrc && src) {
      mobSrc = src
    } else if (!src && mobSrc) {
      src = mobSrc
    }

    /**
     * Get play/pause icons and text from window.theme.collection.
     */
    const { playIcon, pauseIcon, playText, pauseText } = window.theme.collection;

    /**
     * Get the media content based on the type.
     */
    let mediaContent;

    if (type === 'video') {
      mediaContent = `
        <video
          class="in-grid-banner__media"
          autoplay
          playsinline
          muted
          loop
          controls
        >
          <source media="(max-width: 1023px)" src="${mobSrc}">
          <source media="(min-width: 1024px)" src="${src}">
        </video>

        <button
          class="in-grid-banner__play-pause-button"
          aria-label="${pauseText}"
        >
          ${pauseIcon}
        </button>
      `;

    } else {
      mediaContent = `
        <picture>
          <source media="(max-width: 1023px)" srcset="${mobSrc}">
          <source media="(min-width: 1024px)" srcset="${src}">
          <img class="in-grid-banner__media" src="${src}" alt="${title}">
        </picture>
      `;
    }

    /**
     * Set the inner HTML of the element.
     */
    this.innerHTML = `
      <div class="in-grid-banner">
        <div class="in-grid-banner__media-container">
          ${mediaContent}
        </div>
        <div class="in-grid-banner__content">
          <div class="in-grid-banner__content-title h2">${title}</div>
          <div class="in-grid-banner__content-text">${subtitle}</div>
          ${ctaLabel && ctaUrl ? `<a href="${ctaUrl}"><button class="button button--${ctaStyle === 'dark' ? 'secondary' : 'light'} in-grid-banner__content-link">${ctaLabel}</button></a>` : ''}
        </div>
      </div>
    `;

    /**
     * Add event listener for play/pause button if type is video.
     */
    if (type === 'video') {
      const video = this.querySelector('video');
      const playPauseButton = this.querySelector('.in-grid-banner__play-pause-button');

      playPauseButton.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          playPauseButton.innerHTML = pauseIcon;
          playPauseButton.setAttribute('aria-label', pauseText);
        } else {
          video.pause();
          playPauseButton.innerHTML = playIcon;
          playPauseButton.setAttribute('aria-label', playText);
        }
      });
    }
  }
}

/**
 * Define the custom element if it hasn't been defined already.
 */
if (!customElements.get('in-grid-banner')) {
  customElements.define('in-grid-banner', InGridBanner);
}
